import type { Timestamp } from 'firebase/firestore';
import type { ItemType } from './index';

// ============================================================================
// ORGANIZATION TYPES
// ============================================================================

export type UserRole = 'superAdmin' | 'admin' | 'standard';
export type InvitableRole = 'admin' | 'standard'; // Roles that can be assigned via invitation
export type AccountType = 'bank' | 'credit_card' | 'cash' | 'digital_wallet' | 'other';
export type InvitationStatus = 'pending' | 'accepted' | 'expired';
export type MemberStatus = 'active' | 'inactive';

/**
 * Organization - represents a family/household workspace
 */
export interface Organization {
  id: string;
  name: string;
  createdBy: string;
  createdAt: Timestamp;
  updatedAt?: Timestamp;
}

/**
 * OrganizationMember - user membership in an organization
 */
export interface OrganizationMember {
  userId: string;
  displayName?: string;
  email?: string;
  photoURL?: string;
  role: UserRole;
  joinedAt: Timestamp;
  status: MemberStatus;
}

/**
 * Invitation - pending invitation to join an organization
 * Note: superAdmin cannot be assigned via invitation, only via org creation
 */
export interface Invitation {
  id: string;
  email: string;
  role: InvitableRole;
  invitedBy: string;
  inviterName?: string;
  token: string;
  status: InvitationStatus;
  createdAt: Timestamp;
  expiresAt: Timestamp;
}

// ============================================================================
// CATEGORY TYPES
// ============================================================================

/**
 * SubCategory - nested category
 */
export interface SubCategory {
  id: string;
  name: string;
}

/**
 * Category - organization-level category for items
 */
export interface Category {
  id: string;
  name: string;
  isDefault: boolean;
  itemTypes: ItemType[];  // Which item types this category applies to
  subCategories: SubCategory[];
  color?: string;
  order: number;
  createdAt: Timestamp;
  updatedAt?: Timestamp;
}

/**
 * Input for creating a new Category
 */
export interface CreateCategoryInput {
  name: string;
  itemTypes: ItemType[];
  subCategories?: string[];  // Just names, IDs generated on create
  color?: string;
}

/**
 * Input for updating a Category
 */
export interface UpdateCategoryInput {
  name?: string;
  itemTypes?: ItemType[];
  color?: string;
}

// ============================================================================
// ENTITY TYPES (banks, institutions, counterparties)
// ============================================================================

/**
 * Entity - a bank, institution, or counterparty (e.g., "Chase", "Amazon")
 */
export interface Entity {
  id: string;
  name: string;
  isActive: boolean;
  createdAt: Timestamp;
  updatedAt?: Timestamp;
}

/**
 * Input for creating a new Entity
 */
export interface CreateEntityInput {
  name: string;
}

/**
 * Input for updating an Entity
 */
export interface UpdateEntityInput {
  name?: string;
  isActive?: boolean;
}

// ACCOUNT TYPES (for transactions)
// ============================================================================

/**
 * Account - bank account, credit card, or payment method linked to an entity
 */
export interface Account {
  id: string;
  entityId: string;
  accountName: string;
  accountNumber?: string;
  type: AccountType;
  isDefault: boolean;
  lastUsed?: Timestamp;
  isActive: boolean;
  createdAt: Timestamp;
  updatedAt?: Timestamp;
}

/**
 * Input for creating a new Account
 */
export interface CreateAccountInput {
  entityId: string;
  accountName: string;
  accountNumber?: string;
  type: AccountType;
  isDefault?: boolean;
}

/**
 * Input for updating an Account
 */
export interface UpdateAccountInput {
  entityId?: string;
  accountName?: string;
  accountNumber?: string;
  type?: AccountType;
  isActive?: boolean;
}

// ============================================================================
// DEFAULT CATEGORIES
// ============================================================================

/**
 * Default categories to seed for new organizations
 */
export const DEFAULT_CATEGORIES: Omit<CreateCategoryInput, 'color'>[] = [
  { name: 'Personal', itemTypes: ['task', 'transaction', 'note', 'keyDate'] },
  { name: 'Work', itemTypes: ['task', 'transaction', 'note', 'keyDate'] },
  {
    name: 'Finance',
    itemTypes: ['task', 'transaction', 'note'],
    subCategories: ['Bills', 'Savings', 'Investments']
  },
  {
    name: 'Food & Dining',
    itemTypes: ['transaction'],
    subCategories: ['Groceries', 'Restaurants', 'Coffee', 'Delivery']
  },
  {
    name: 'Transportation',
    itemTypes: ['transaction'],
    subCategories: ['Gas', 'Parking', 'Transit', 'Rideshare', 'Maintenance']
  },
  {
    name: 'Shopping',
    itemTypes: ['transaction'],
    subCategories: ['Clothing', 'Electronics', 'Home', 'Online']
  },
  {
    name: 'Healthcare',
    itemTypes: ['task', 'transaction', 'keyDate'],
    subCategories: ['Doctor', 'Pharmacy', 'Insurance']
  },
  {
    name: 'Family',
    itemTypes: ['task', 'note', 'keyDate'],
    subCategories: ['Kids', 'Pets', 'Events']
  },
  {
    name: 'Entertainment',
    itemTypes: ['transaction', 'keyDate'],
    subCategories: ['Movies', 'Games', 'Sports', 'Travel']
  },
  {
    name: 'Education',
    itemTypes: ['task', 'transaction', 'note'],
    subCategories: ['Courses', 'Books', 'Supplies']
  },
  {
    name: 'Bookmarks',
    itemTypes: ['note'],
    subCategories: ['GenAI', 'Stocks', 'News', 'Cyber', 'Education', 'Careers']
  },
];

// ============================================================================
// CATEGORY COLORS
// ============================================================================

/**
 * Available colors for category badges
 * Using -200 shades for better visibility (Google Keep-inspired pastels)
 */
export const CATEGORY_COLORS = [
  { value: 'gray', label: 'Gray', bg: 'bg-gray-200', text: 'text-gray-700' },
  { value: 'red', label: 'Red', bg: 'bg-red-200', text: 'text-red-800' },
  { value: 'orange', label: 'Orange', bg: 'bg-orange-200', text: 'text-orange-800' },
  { value: 'amber', label: 'Amber', bg: 'bg-amber-200', text: 'text-amber-800' },
  { value: 'yellow', label: 'Yellow', bg: 'bg-yellow-200', text: 'text-yellow-800' },
  { value: 'lime', label: 'Lime', bg: 'bg-lime-200', text: 'text-lime-800' },
  { value: 'green', label: 'Green', bg: 'bg-green-200', text: 'text-green-800' },
  { value: 'teal', label: 'Teal', bg: 'bg-teal-200', text: 'text-teal-800' },
  { value: 'cyan', label: 'Cyan', bg: 'bg-cyan-200', text: 'text-cyan-800' },
  { value: 'blue', label: 'Blue', bg: 'bg-blue-200', text: 'text-blue-800' },
  { value: 'indigo', label: 'Indigo', bg: 'bg-indigo-200', text: 'text-indigo-800' },
  { value: 'purple', label: 'Purple', bg: 'bg-purple-200', text: 'text-purple-800' },
  { value: 'pink', label: 'Pink', bg: 'bg-pink-200', text: 'text-pink-800' },
];

// ============================================================================
// ACCOUNT TYPE ICONS/LABELS
// ============================================================================

/**
 * Account type display configuration
 */
export const ACCOUNT_TYPES: { value: AccountType; label: string; icon: string }[] = [
  { value: 'bank', label: 'Bank Account', icon: 'Landmark' },
  { value: 'credit_card', label: 'Credit Card', icon: 'CreditCard' },
  { value: 'cash', label: 'Cash', icon: 'Banknote' },
  { value: 'digital_wallet', label: 'Digital Wallet', icon: 'Wallet' },
  { value: 'other', label: 'Other', icon: 'CircleDollarSign' },
];

// ============================================================================
// USER PREFERENCES (for settings store integration)
// ============================================================================

/**
 * User preferences stored on User document
 */
export interface UserPreferences {
  defaultAccountId?: string;
  showPersonalItemsOnly?: boolean;
}
